import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnInit,
  untracked,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of } from 'rxjs';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { InputAddressComponent } from '@core/components/forms/input-address/input-address.component';
import { ClientConfigurationStatus } from '@core/models/client.model';
import { ClientStore } from '@core/state/client/client.store';
import { ClientsService } from '@core/services/clients/clients.service';
import { AddressesService } from '@core/services/addresses/addresses.service';
import { EnvironmentService } from '@core/services/environment/environment.service';
import { PurchaseFlowService } from '@features/purchase-flow/application/purchase-flow/purchase-flow.service';
import { PurchaseFlowPrepayProgressService } from '@features/purchase-flow/application/purchase-flow-progress/purchase-flow-prepay-progress.service';
import { PurchaseStore } from '@features/purchase-flow/state/purchase.store';
import { Location } from '@features/purchase-flow/domain/models/location.model';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { AuthStore } from '@features/authentication/state/auth.store';

@Component({
  selector: 'd2d-widget-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputAddressComponent,
    ButtonModule,
    FormsModule,
    SkeletonModule,
    TranslocoPipe,
  ],
  templateUrl: './widget-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './widget-search.component.scss',
})
export class WidgetSearchComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  purchaseFlowService = inject(PurchaseFlowService);
  private readonly prepayProgressService = inject(PurchaseFlowPrepayProgressService);
  private readonly purchaseStore = inject(PurchaseStore);
  private readonly clientStore = inject(ClientStore);
  private readonly clientsService = inject(ClientsService);
  private readonly addressesService = inject(AddressesService);
  private readonly messageService = inject(MessageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly logger = inject(LoggerService).forContext('WidgetSearchComponent');

  private readonly authStore = inject(AuthStore);
  private readonly cdr = inject(ChangeDetectorRef);

  public clientConfig = this.clientStore.clientConfig;

  /**
   * Configuration status for root route (when no clientConfig).
   * Loaded internally by the widget when on root route.
   */
  private rootConfigurationStatus: ClientConfigurationStatus | null = null;

  /**
   * Explicit loading flag for configuration status.
   * True while waiting for the API response on root route.
   */
  public isLoadingConfig = true;

  /**
   * Explicit loading flag for client shipping address.
   * True while waiting for the API response if address is not in config.
   */
  public isLoadingAddress = false;

  get tripType(): 'one_way' | 'round_trip' {
    return this.shipmentType?.value === 'one_way' ? 'one_way' : 'round_trip';
  }

  helperDropdownDestination = true;
  helperDropdownOrigin = true;

  /**
   * Lado (origin/destination) donde reside actualmente la dirección del cliente.
   * Se actualiza al hacer switch para que el bloqueo "viaje" con la dirección.
   * Delega en purchaseFlowService (singleton) en vez de ser un campo propio:
   * WidgetSearchComponent se destruye/recrea al navegar a select-dates y
   * volver ("atrás"), y un campo de instancia perdería el swap ya hecho,
   * provocando que validateIfIsClient() bloquee/fuerce el país del lado
   * equivocado (el país se sobrescribe pero el código postal no la sigue).
   */
  private get clientAddressSlot(): 'origin' | 'destination' {
    return this.purchaseFlowService.clientAddressSlot;
  }
  private set clientAddressSlot(value: 'origin' | 'destination') {
    this.purchaseFlowService.clientAddressSlot = value;
  }

  account = computed(() => this.authStore.currentAccount());
  isLogged = computed(() => this.account() !== null);

  /**
   * Dirección principal guardada por el usuario logueado (agenda de
   * direcciones), usada como valor por defecto de origen/destino cuando
   * el campo está vacío. Solo aplica si el usuario está logueado y tiene
   * una dirección marcada como principal.
   */
  private userPrimaryAddress: Location | null = null;
  private userPrimaryAddressRequested = false;
  private userPrimaryAddressApplied = false;

  constructor() {
    effect(() => {
      const config = this.clientStore.clientConfig();
      this.isLoadingAddress = !!config && !this.clientStore.primaryAddress();

      this.helperDropdownOrigin = true;
      this.helperDropdownDestination = true;
      const isLogged = this.isLogged();

      untracked(() => {
        this.validateIfIsClient();
        if (isLogged) {
          this.loadUserPrimaryAddress();
        }
        this.applyUserPrimaryAddressIfNeeded();
        this.ensureDefaultFixedCountry();
      });
    });
  }

  ngOnInit(): void {
    if (this.shipmentType.value !== 'one_way' && this.shipmentType.value !== 'round_trip') {
      this.shipmentType.setValue('round_trip');
    }
    this.loadConfigurationStatus();
  }

  /**
   * Load configuration status.
   * For root route: calls API with client_id=0
   * For client routes: uses existing config from resolver
   * Skips API call during SSR (server-side rendering)
   */
  private loadConfigurationStatus(): void {
    // Skip during SSR - only run in browser
    if (this.environmentService.isServer()) {
      return;
    }

    // For client routes, configuration is already loaded by resolver
    if (this.clientConfig()) {
      this.isLoadingConfig = false;
      return;
    }

    // For root route, load default configuration
    this.isLoadingConfig = true;
    this.clientsService.getConfigurationStatus(0).subscribe({
      next: (status) => {
        this.rootConfigurationStatus = status;
        this.isLoadingConfig = false;
      },
      error: (error) => {
        this.logger.warn('Error fetching root configuration status:', error);
        // On error, set a fail-closed status (widget blocked)
        this.rootConfigurationStatus = {
          client_id: 0,
          has_transport_companies: false,
          has_payment_methods: false,
          error: true,
        };
        this.isLoadingConfig = false;
      },
    });
  }

  get origin(): FormControl {
    return this.purchaseFlowService.routes.at(0).get('from') as FormControl;
  }

  get destination(): FormControl {
    return this.purchaseFlowService.routes.at(0).get('to') as FormControl;
  }

  get shipmentType(): FormControl {
    return this.purchaseFlowService.shipmentCreateForm.get('shipment_type') as FormControl;
  }

  /**
   * El país recibido de shipping-address queda fijo y acompaña a la
   * dirección del cliente al intercambiar origen y destino.
   */
  private get clientFixedCountryCode(): string | null {
    const config = this.clientConfig();
    if (!config) return null;

    const address = this.clientStore.primaryAddress();
    return address?.country?.trim() || address?.country_code?.trim() || null;
  }

  /**
   * Nivel 1 (dirección asignada completa, ej. hoteles): el país fijo se
   * mantiene atado al lado designado por clientAddressSlot, igual que el
   * resto de la dirección (que ya queda disabled() en validateIfIsClient()).
   */
  private get isFixedAddressClient(): boolean {
    return this.clientConfig()?.is_fixed ?? false;
  }

  get originFixedCountryCode(): string | null {
    if (this.isFixedAddressClient) {
      return this.clientAddressSlot === 'origin' ? this.clientFixedCountryCode : null;
    }
    return this.reactiveCountryLock().origin;
  }

  get destinationFixedCountryCode(): string | null {
    if (this.isFixedAddressClient) {
      return this.clientAddressSlot === 'destination' ? this.clientFixedCountryCode : null;
    }
    return this.reactiveCountryLock().destination;
  }

  /**
   * Nivel 2 (sin dirección asignada completa, solo país de referencia): el
   * bloqueo ya no viaja con un "slot" fijo sino que se recalcula a partir de
   * los valores reales de ambos selectores en cada ciclo de detección de
   * cambios. Reglas (ver tarea-bloqueo-selects.md):
   * - Ambos en el país fijo -> ambos habilitados.
   * - Uno distinto del país fijo -> se bloquea el que todavía lo conserva.
   */
  private reactiveCountryLock(): { origin: string | null; destination: string | null } {
    const fixedCountry = this.clientFixedCountryCode;
    if (!fixedCountry) {
      return { origin: null, destination: null };
    }

    const originVal = this.extractCountryCode(this.origin.value);
    const destVal = this.extractCountryCode(this.destination.value);

    if (originVal === fixedCountry && destVal === fixedCountry) {
      return { origin: null, destination: null };
    }
    if (originVal === fixedCountry && destVal !== fixedCountry) {
      return { origin: fixedCountry, destination: null };
    }
    if (destVal === fixedCountry && originVal !== fixedCountry) {
      return { origin: null, destination: fixedCountry };
    }
    return { origin: null, destination: null };
  }

  private extractCountryCode(value: Location | null): string | null {
    if (!value) return null;
    return value.country_code?.trim() || value.country?.trim() || null;
  }

  /**
   * Por defecto (sin dirección asignada completa), ambos selectores deben
   * arrancar mostrando el país fijo, aunque el resto de la dirección no se
   * prellene (ej. shipping_prefill_address=false). Sin esto, un campo recién
   * creado sin país seleccionado nunca coincide con el país fijo y la regla
   * reactiva no tiene nada que bloquear/mostrar -> el selector aparece vacío
   * en vez de mostrar el país fijo desbloqueado (regla 1 de la tarea).
   * Solo completa el país: no toca city/postal_code/etc. si ya existían.
   */
  private ensureDefaultFixedCountry(): void {
    if (this.isFixedAddressClient) return;

    const fixedCountry = this.clientFixedCountryCode;
    if (!fixedCountry) return;

    if (!this.extractCountryCode(this.origin.value)) {
      this.setDefaultCountry(this.origin, fixedCountry);
    }
    if (!this.extractCountryCode(this.destination.value)) {
      this.setDefaultCountry(this.destination, fixedCountry);
    }
  }

  private setDefaultCountry(control: FormControl, countryCode: string): void {
    const current: Partial<Location> = control.value ?? {};
    control.setValue({
      ...current,
      country: countryCode,
      country_code: countryCode,
      city: current.city ?? '',
      state: current.state ?? '',
      postal_code: current.postal_code ?? '',
    });
  }

  /**
   * Get the effective configuration status.
   * Uses clientConfig.configuration_status for client routes,
   * or rootConfigurationStatus for root route.
   */
  private get effectiveConfigurationStatus(): ClientConfigurationStatus | null | undefined {
    // For client routes, use the clientConfig's configuration_status
    const config = this.clientConfig();
    if (config) {
      return config.configuration_status;
    }
    // For root route, use the rootConfigurationStatus Input
    return this.rootConfigurationStatus;
  }

  get isConfigurationComplete(): boolean {
    const status = this.effectiveConfigurationStatus;
    // Si no hay status (cliente sin validación), bloquear por seguridad
    if (!status) return false;
    // Si hubo error en la llamada, bloquear
    if (status.error) return false;
    // Verificar que ambas configuraciones estén completas
    return status.has_transport_companies && status.has_payment_methods;
  }

  get hasConfigurationError(): boolean {
    return this.effectiveConfigurationStatus?.error === true;
  }

  /**
   * Check if the configuration status is still loading.
   * Returns true while waiting for the API response.
   * Always returns true during SSR to show skeleton.
   */
  get isLoading(): boolean {
    // During SSR, always show skeleton
    if (this.environmentService.isServer()) {
      return true;
    }
    return this.isLoadingConfig || this.isLoadingAddress;
  }

  setTripType(type: 'one_way' | 'round_trip') {
    this.shipmentType.setValue(type);
  }

  switchInputsValues(): void {
    const originValue = this.origin.value;
    const destinationValue = this.destination.value;

    const config = this.clientConfig();
    if (config) {
      // El país fijo del cliente (clientFixedCountryCode) siempre viaja con
      // el cliente al hacer swap, sin importar si el resto de la dirección
      // también está fija (config.is_fixed) o no.
      this.clientAddressSlot = this.clientAddressSlot === 'origin' ? 'destination' : 'origin';

      // d2d-input-address recibe el país fijo por un @Input() señal
      // (originFixedCountryCode/destinationFixedCountryCode) que Angular
      // solo empuja al hijo en el siguiente ciclo de detección de cambios.
      // Forzamos ese ciclo aquí, antes de mover los valores, para que el
      // statusChanges síncrono que dispara el swap más abajo (al llamar
      // setValue/enable/disable) ya lea el país fijo del lado correcto en
      // vez de uno obsoleto -si no, el país fijo se queda "pegado" al lado
      // que lo tenía antes del swap y además reaparece en el otro lado,
      // porque el lado recién liberado también reaplica ese valor viejo.
      this.cdr.detectChanges();

      if (config.is_fixed) {
        if (this.clientAddressSlot === 'origin') {
          this.origin.disable();
          this.helperDropdownOrigin = false;
          this.destination.enable();
          this.helperDropdownDestination = true;
        } else {
          this.destination.disable();
          this.helperDropdownDestination = false;
          this.origin.enable();
          this.helperDropdownOrigin = true;
        }
      }
    }

    this.origin.setValue(destinationValue);
    this.destination.setValue(originValue);
  }

  goToPurchase(): void {
    this.origin.markAsTouched();
    this.destination.markAsTouched();

    if (this.origin.invalid || this.destination.invalid) {
      return;
    }

    // Validar que los códigos postales no sean iguales
    const originPostalCode = this.origin.value?.postal_code?.trim().toLowerCase();
    const destPostalCode = this.destination.value?.postal_code?.trim().toLowerCase();

    if (originPostalCode && destPostalCode && originPostalCode === destPostalCode) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translocoService.translate('search_widget.samePostalCodeSummary'),
        detail: this.translocoService.translate('search_widget.samePostalCodeDetail'),
        life: 5000,
      });
      return;
    }

    // Guardar temporalmente los valores
    const currentOrigin = this.origin.value;
    const currentDestination = this.destination.value;
    const currentTripType = this.tripType;

    // Limpiar el estado de compra al iniciar un nuevo flujo desde el widget
    this.purchaseFlowService.resetForm();
    this.prepayProgressService.clear();
    this.purchaseStore.resetFlow();
    sessionStorage.removeItem('purchase');

    // Restaurar los valores de origen y destino después del reset
    this.origin.setValue(currentOrigin);
    this.destination.setValue(currentDestination);
    this.shipmentType.setValue(currentTripType);

    const routesLength = this.purchaseFlowService.routes.length;

    // Si el tipo de viaje es round_trip, se agrega una segunda ruta como vuelta
    if (this.tripType === 'round_trip' && routesLength === 1) {
      this.purchaseFlowService.addRoute('Vuelta');

      this.purchaseFlowService.routes.at(1).get('from')?.setValue(currentDestination);
      this.purchaseFlowService.routes.at(1).get('to')?.setValue(currentOrigin);
    }

    if (this.tripType === 'one_way' && routesLength === 2) {
      this.purchaseFlowService.removeRoute(1);
    }

    this.router.navigate(['purchase'], { relativeTo: this.route });
  }

  validateIfIsClient(): void {
    // Si no hay configuración de cliente, no hacemos nada
    const config = this.clientConfig();
    if (!config) return;

    // Nivel 1: la dirección se considera fija únicamente cuando el staff lo
    // decidió (config.is_fixed). Ya no depende del tipo de cliente (client_type).
    const isFixedAddressClient = config.is_fixed ?? false;

    // Nivel 2: solo aplica cuando el cliente NO tiene dirección fija.
    // Decide si se muestra la dirección de shipping_data prellenada o vacía.
    const shouldPrefillAddress = config.shipping_prefill_address ?? true;

    // El campo objetivo es el que actualmente representa la dirección del cliente
    // (origin o destination, según el último switch realizado por el usuario).
    const isTargetOrigin = isFixedAddressClient && this.clientAddressSlot === 'origin';
    const targetControl = isTargetOrigin ? this.origin : this.destination;
    const otherControl = isTargetOrigin ? this.destination : this.origin;

    otherControl.enable();
    targetControl.disable();

    // Usar el método utilitario para obtener la dirección principal
    const primaryAddress = this.clientStore.primaryAddress();

    // Si el campo objetivo ya tiene un valor (indicado por el código postal),
    // no lo sobrescribimos con la dirección principal del cliente, solo ajustamos si puede editarse.
    if (targetControl.value && targetControl.value.postal_code) {
      if (!isFixedAddressClient) {
        targetControl.enable();
      }
      this.setHelperDropdown(isTargetOrigin, !targetControl.disabled);
      this.isLoadingAddress = false;
      return; // No continuar para establecer desde la configuración del cliente
    }

    // Nivel 2: cliente sin dirección fija que optó por no prellenar ->
    // el campo queda vacío y editable (no se muestra la dirección).
    if (!isFixedAddressClient && !shouldPrefillAddress) {
      targetControl.enable();
      this.setHelperDropdown(isTargetOrigin, true);
      this.isLoadingAddress = false;
      return;
    }

    if (primaryAddress) {
      const address = {
        city: primaryAddress.city,
        postal_code: primaryAddress.postal_code,
        country: primaryAddress.country,
        state: primaryAddress.state,
        address_line1: primaryAddress.address_line1,
        address_line2: primaryAddress.address_line2,
        country_code: primaryAddress.country_code,
        business_name: primaryAddress.business_name,
      };

      targetControl.setValue(address);
      if (!isFixedAddressClient) {
        targetControl.enable();
      }
      this.setHelperDropdown(isTargetOrigin, !targetControl.disabled);
      return;
    }

    // No hay dirección de cliente para prellenar (cliente fijo sin shipping data,
    // o no fijo con prefill activo): se deja editable salvo que la dirección sea fija.
    if (!isFixedAddressClient) {
      targetControl.enable();
    }
    this.setHelperDropdown(isTargetOrigin, !targetControl.disabled);

    // Fallback: Si no hay direcciones en la configuración, hacer la petición directa
    const slug = config.slug;
    if (!slug) {
      // Sin slug no hay forma de obtener la dirección del cliente: dejamos el
      // campo editable en vez de deshabilitado y vacío.
      targetControl.enable();
      this.setHelperDropdown(isTargetOrigin, true);
      this.applyUserPrimaryAddressIfNeeded();
      return;
    }

    this.isLoadingAddress = true;
    this.clientsService.getClientShippingAddress(slug).subscribe({
      next: (address) => {
        // Volver a comprobar por si el formulario se rellenó mientras la llamada asíncrona estaba en curso.
        if (targetControl.value && targetControl.value.postal_code) {
          this.isLoadingAddress = false;
          if (!isFixedAddressClient) {
            targetControl.enable();
          }
          this.setHelperDropdown(isTargetOrigin, !targetControl.disabled);
          return;
        }
        const location = {
          city: address.city,
          postal_code: address.postal_code,
          country: address.country,
          state: address.state,
          address_line1: address.address_line1,
          address_line2: address.address_line2,
          country_code: address.country_code,
          business_name: address.business_name,
        };

        targetControl.setValue(location);
        if (!isFixedAddressClient) {
          targetControl.enable();
        }
        this.setHelperDropdown(isTargetOrigin, !targetControl.disabled);
        this.applyUserPrimaryAddressIfNeeded();
      },
      error: (error) => {
        this.logger.error('Error fetching client shipping address:', error);
        this.isLoadingAddress = false;
        // No hay shipping data disponible para este cliente (p. ej. 404):
        // dejamos el campo editable en vez de deshabilitado y vacío.
        targetControl.enable();
        this.setHelperDropdown(isTargetOrigin, true);
        this.applyUserPrimaryAddressIfNeeded();
      },
      complete: () => {
        this.isLoadingAddress = false;
        this.ensureDefaultFixedCountry();
      },
    });
  }

  private setHelperDropdown(isTargetOrigin: boolean, showDropdown: boolean): void {
    if (isTargetOrigin) {
      this.helperDropdownOrigin = showDropdown;
    } else {
      this.helperDropdownDestination = showDropdown;
    }
  }

  /**
   * Obtiene la dirección principal del usuario logueado (una sola vez) para
   * usarla como valor por defecto de origen/destino. Si el usuario no tiene
   * direcciones guardadas o ninguna marcada como principal, no hace nada.
   */
  private loadUserPrimaryAddress(): void {
    if (this.userPrimaryAddressRequested) return;
    this.userPrimaryAddressRequested = true;

    this.addressesService
      .getMyPrimaryAddress()
      .pipe(catchError(() => of(null)))
      .subscribe((primaryAddress) => {
        if (!primaryAddress) return;

        this.userPrimaryAddress = {
          city: primaryAddress.city,
          postal_code: primaryAddress.postal_code,
          country: primaryAddress.country,
          state: primaryAddress.state || '',
          address_line1: primaryAddress.address_line1,
          address_line2: primaryAddress.address_line2,
          country_code: primaryAddress.country_code,
        };
        this.applyUserPrimaryAddressIfNeeded();
      });
  }

  /**
   * Rellena con la dirección principal del usuario el primer campo
   * (origen o destino) que esté vacío y habilitado. No sobrescribe un
   * campo que ya tenga valor (p. ej. la dirección fija de un cliente) ni
   * uno deshabilitado, y solo se aplica una vez por carga del widget.
   */
  private applyUserPrimaryAddressIfNeeded(): void {
    if (this.userPrimaryAddressApplied || !this.userPrimaryAddress) return;

    const isOriginEmpty = !this.origin.value?.postal_code;
    const isDestinationEmpty = !this.destination.value?.postal_code;

    if (!this.origin.disabled && isOriginEmpty) {
      this.origin.setValue(this.userPrimaryAddress);
      this.userPrimaryAddressApplied = true;
    } else if (!this.destination.disabled && isDestinationEmpty) {
      this.destination.setValue(this.userPrimaryAddress);
      this.userPrimaryAddressApplied = true;
    }
  }
}
